import {
  addMonths,
  differenceInDays,
  differenceInMilliseconds,
  endOfDay,
  format,
  isValid,
  parseISO,
  startOfDay,
} from 'date-fns';

import {
  EncounterSchema,
  MedicalProvider,
} from '../../../services/types/treatment-types';

/**
 * Safely parse a date string
 */
export const parseDate = (dateString: string | null): Date => {
  if (!dateString) {
    return new Date();
  }

  try {
    return startOfDay(parseISO(dateString));
  } catch (e) {
    console.error('Error parsing date:', dateString, e);
    return new Date();
  }
};

/**
 * Check if date string is valid
 */
export const isValidDate = (dateString: string | null): boolean => {
  if (!dateString) return false;

  try {
    const date = parseISO(dateString);
    return isValid(date);
  } catch (e) {
    return false;
  }
};

/**
 * Get all unique encounters from all providers sorted by date
 */
export const getAllEncountersSorted = (
  providers: MedicalProvider[],
): EncounterSchema[] => {
  // Get all encounters with valid dates
  const allEncounters = providers
    .flatMap((provider) =>
      (provider.encounters || []).map((encounter) => ({
        ...encounter,
        providerId: provider.id,
        providerName: provider.name || 'Unnamed Provider',
      })),
    )
    .filter((encounter) => encounter.date && isValidDate(encounter.date));

  // Sort by date (earliest first)
  return allEncounters.sort((a, b) => {
    const dateA = parseDate(a.date as string);
    const dateB = parseDate(b.date as string);
    return dateA.getTime() - dateB.getTime();
  });
};

/**
 * Calculate the initial full time range based on all encounters.
 */
export const calculateFullTimeRange = (
  providers: MedicalProvider[],
  paddingMonths = 3, // Increased to 3 months of padding
): [Date, Date] => {
  const allEncounters = getAllEncountersSorted(providers);

  if (allEncounters.length === 0) {
    const today = new Date();
    const sixMonthsAgo = addMonths(today, -6);
    return [startOfDay(sixMonthsAgo), endOfDay(today)];
  }

  const firstDate = parseDate(allEncounters[0].date as string);
  const lastDate = parseDate(
    allEncounters[allEncounters.length - 1].date as string,
  );

  const startDateWithPadding = addMonths(firstDate, -paddingMonths);
  const endDateWithPadding = addMonths(lastDate, paddingMonths);

  return [startOfDay(startDateWithPadding), endOfDay(endDateWithPadding)];
};

/**
 * Calculate position percentage for a date within a *visible* date range.
 */
export const calculateDatePosition = (
  date: Date,
  visibleStartDate: Date,
  visibleEndDate: Date,
): number => {
  if (
    !isValid(date) ||
    !isValid(visibleStartDate) ||
    !isValid(visibleEndDate) ||
    visibleEndDate <= visibleStartDate
  ) {
    return 0; // Avoid division by zero or NaNs
  }
  const totalVisibleDuration = differenceInMilliseconds(
    visibleEndDate,
    visibleStartDate,
  );
  const durationFromVisibleStart = differenceInMilliseconds(
    date,
    visibleStartDate,
  );

  const position = (durationFromVisibleStart / totalVisibleDuration) * 100;
  return Math.max(0, Math.min(100, position)); // Clamp between 0 and 100
};

/**
 * Calculate the visible time range for the timeline based on zoom level and center date.
 * Zoom level: 10 = zoomed out (full range), 100 = zoomed in (smallest range).
 * Center date: The date to center the visible window on (null means use middle of full range)
 */
export const calculateVisibleTimeRange = (
  fullStartDate: Date,
  fullEndDate: Date,
  zoomLevel: number, // 10 to 100
  centerDate?: Date | null, // Optional center date for panning
): [Date, Date] => {
  if (!isValid(fullStartDate) || !isValid(fullEndDate)) {
    const today = new Date();
    return [startOfDay(addMonths(today, -6)), endOfDay(today)];
  }

  const totalDurationMs = differenceInMilliseconds(fullEndDate, fullStartDate);

  // Higher zoom means smaller visible window
  const visibleDurationMs = totalDurationMs * (1 / (zoomLevel / 10));

  // Determine center point based on centerDate or default to middle
  const centerPointMs =
    centerDate && isValid(centerDate)
      ? centerDate.getTime()
      : fullStartDate.getTime() + totalDurationMs / 2;

  // Calculate window start/end from center point
  let visibleStartMs = centerPointMs - visibleDurationMs / 2;
  let visibleEndMs = centerPointMs + visibleDurationMs / 2;

  // Clamp to full range
  const originalVisibleStartMs = visibleStartMs;
  const originalVisibleEndMs = visibleEndMs;

  visibleStartMs = Math.max(visibleStartMs, fullStartDate.getTime());
  visibleEndMs = Math.min(visibleEndMs, fullEndDate.getTime());

  // Ensure minimum visible duration (1 month)
  const minDuration = differenceInMilliseconds(
    addMonths(new Date(), 1),
    new Date(),
  );

  if (visibleEndMs - visibleStartMs < minDuration) {
    // If too zoomed in, force a reasonable minimum window
    if (centerDate) {
      // Center around the specified date
      visibleStartMs = centerDate.getTime() - minDuration / 2;
      visibleEndMs = centerDate.getTime() + minDuration / 2;

      // Re-clamp to range boundaries
      visibleStartMs = Math.max(visibleStartMs, fullStartDate.getTime());
      visibleEndMs = Math.min(visibleEndMs, fullEndDate.getTime());

      // If still too small, adjust to ensure minimum window size
      if (visibleEndMs - visibleStartMs < minDuration) {
        if (visibleStartMs === fullStartDate.getTime()) {
          visibleEndMs = Math.min(
            visibleStartMs + minDuration,
            fullEndDate.getTime(),
          );
        } else {
          visibleStartMs = Math.max(
            visibleEndMs - minDuration,
            fullStartDate.getTime(),
          );
        }
      }
    } else {
      // No center date, just ensure minimum window from start or end
      const atStartBoundary = visibleStartMs === fullStartDate.getTime();

      if (atStartBoundary) {
        visibleEndMs = Math.min(
          visibleStartMs + minDuration,
          fullEndDate.getTime(),
        );
      } else {
        visibleStartMs = Math.max(
          visibleEndMs - minDuration,
          fullStartDate.getTime(),
        );
      }
    }
  }

  // If clamping happened, maintain the original visible window size when possible
  const visibleDuration = originalVisibleEndMs - originalVisibleStartMs;

  // If we hit the start boundary but not the end, extend end to maintain window size
  if (
    visibleStartMs === fullStartDate.getTime() &&
    originalVisibleStartMs < fullStartDate.getTime()
  ) {
    visibleEndMs = Math.min(
      fullStartDate.getTime() + visibleDuration,
      fullEndDate.getTime(),
    );
  }

  // If we hit the end boundary but not the start, extend start to maintain window size
  if (
    visibleEndMs === fullEndDate.getTime() &&
    originalVisibleEndMs > fullEndDate.getTime()
  ) {
    visibleStartMs = Math.max(
      fullEndDate.getTime() - visibleDuration,
      fullStartDate.getTime(),
    );
  }

  return [new Date(visibleStartMs), new Date(visibleEndMs)];
};

/**
 * Find treatment gaps (periods > gapThresholdDays between encounters)
 */
export const findTreatmentGaps = (
  encounters: EncounterSchema[],
  gapThresholdDays = 30,
): Array<{ start: Date; end: Date; days: number }> => {
  const sortedEncounters = [...encounters].sort((a, b) => {
    const dateA = parseDate(a.date as string);
    const dateB = parseDate(b.date as string);
    return dateA.getTime() - dateB.getTime();
  });

  const gaps = [];

  for (let i = 0; i < sortedEncounters.length - 1; i += 1) {
    const currentDate = parseDate(sortedEncounters[i].date as string);
    const nextDate = parseDate(sortedEncounters[i + 1].date as string);

    const daysBetween = differenceInDays(nextDate, currentDate);

    if (daysBetween > gapThresholdDays) {
      gaps.push({
        start: currentDate,
        end: nextDate,
        days: daysBetween,
      });
    }
  }

  return gaps;
};

/**
 * Format a date string for display
 */
export const formatDateForDisplay = (dateString: string | null): string => {
  if (!dateString) return '—';

  const date = parseDate(dateString);
  if (!isValid(date)) return '—';

  return format(date, 'MMM d, yyyy');
};

/**
 * Generate month markers for the *visible* timeline.
 */
export const generateMonthMarkers = (
  visibleStartDate: Date,
  visibleEndDate: Date,
): Array<{ date: Date; label: string; position: number }> => {
  if (
    !isValid(visibleStartDate) ||
    !isValid(visibleEndDate) ||
    visibleEndDate < visibleStartDate
  ) {
    return [];
  }

  const markers = [];
  let currentDate = new Date(
    visibleStartDate.getFullYear(),
    visibleStartDate.getMonth(),
    1,
  );

  const visibleDurationDays = differenceInDays(
    visibleEndDate,
    visibleStartDate,
  );
  let monthIncrement = 1;
  if (visibleDurationDays > 365 * 2)
    monthIncrement = 6; // Every 6 months for >2 years
  else if (visibleDurationDays > 365)
    monthIncrement = 3; // Every 3 months for >1 year
  else if (visibleDurationDays < 30) monthIncrement = 0; // No month markers if less than a month, show days? (future)

  while (currentDate <= visibleEndDate) {
    if (currentDate >= visibleStartDate) {
      const position = calculateDatePosition(
        currentDate,
        visibleStartDate,
        visibleEndDate,
      );
      markers.push({
        date: new Date(currentDate),
        label: format(
          currentDate,
          monthIncrement >= 3 ? 'yyyy MMM' : 'MMM yyyy',
        ),
        position,
      });
    }
    if (monthIncrement === 0) break; // for daily markers later if needed
    currentDate = addMonths(currentDate, monthIncrement);
  }
  return markers;
};

/**
 * Group encounters by provider
 */
export const groupEncountersByProvider = (
  providers: MedicalProvider[],
): Map<string, EncounterSchema[]> => {
  const providerMap = new Map<string, EncounterSchema[]>();

  providers.forEach((provider) => {
    if (!provider.id) return;

    const encounters = (provider.encounters || [])
      .filter((encounter) => encounter.date && isValidDate(encounter.date))
      .sort((a, b) => {
        const dateA = parseDate(a.date as string);
        const dateB = parseDate(b.date as string);
        return dateA.getTime() - dateB.getTime();
      });

    if (encounters.length > 0) {
      providerMap.set(provider.id, encounters);
    }
  });

  return providerMap;
};
